import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { IntelligenceCard, IntelligenceCardStatus } from './IntelligenceCard';
import { useAtRiskStudents } from '@/hooks/useAtRiskStudents';

export const AtRiskStudentsCard: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, error } = useAtRiskStudents();

  // Determine status based on count
  const getStatus = (count: number): IntelligenceCardStatus => {
    if (count === 0) return 'success';
    if (count <= 3) return 'warning';
    return 'danger';
  };

  // Handle review action
  const handleReviewStudents = () => {
    // Navigate to students page with a filter for at-risk students
    // We'll pass state to indicate filtering for at-risk students
    navigate('/admin/students', { 
      state: { 
        filterAtRisk: true,
        minAttendancePercentage: 60 
      } 
    });
  };

  const count = data?.count || 0;
  const status = getStatus(count);

  // Build context text
  const getContextText = () => {
    if (count === 0) {
      return "All students engaged! 📈";
    }
    
    if (count === 1) {
      return `${data?.students[0]?.first_name} ${data?.students[0]?.last_name} has ${data?.students[0]?.attendance_percentage}% attendance`;
    }
    
    if (count <= 3 && data?.students) {
      const lowestAttendance = Math.min(...data.students.map(s => s.attendance_percentage));
      return `Lowest attendance: ${lowestAttendance}% in last 4 weeks`;
    }
    
    return `${count} students need immediate attention`;
  };

  // Build status text
  const getStatusText = () => {
    if (count === 0) return 'All students engaged';
    if (count === 1) return '1 student at risk';
    if (count <= 3) return `${count} students need attention`;
    return `${count} students at high risk`;
  };

  return (
    <IntelligenceCard
      title="At-Risk Students Alert"
      description="Students with <60% attendance"
      icon={AlertTriangle}
      metric={count}
      metricLabel={count === 1 ? "student at risk" : "students at risk"}
      contextText={getContextText()}
      status={status}
      statusText={getStatusText()}
      actionLabel="Review Students"
      onAction={handleReviewStudents}
      actionDisabled={count === 0}
      isLoading={isLoading}
      error={error?.message}
    />
  );
};